import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from "@angular/core";
import { flatten, sortBy } from "lodash";
import { format, getUnixTime, parse } from "date-fns";
import { TranslateService } from "@ngx-translate/core";
import { GraphUtils } from 'app/frontend/lib/analytics/graphs/utils.service';
import { MoodsByDay, MoodsByTimeRange } from 'app/frontend/lib/analytics/graphs/utils.type';
import { BehaviorSubject } from 'rxjs';
import { ChartConstructorType } from "highcharts-angular";

@Component({
    selector: "mood-distribution-by-day",
    templateUrl: "./mood-distribution-by-day.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MoodDistributionByDayComponent implements OnChanges { 

    @Input() moodsByDay: MoodsByDay[];

    @Output() range: EventEmitter<string> = new EventEmitter();

    constructor(
        private translateService: TranslateService, 
        private graphUtils: GraphUtils
    ) {
    }

    chartOptions$ = new BehaviorSubject<Highcharts.Options>({});
    chartConstructor: ChartConstructorType = 'chart';

    rangeSelector: RangeSelectorType = 'day';

    ngOnChanges() { 
        const chartOptions: Highcharts.Options =  this.bubbleChartConfig(this.moodsByDay, this.averageMoodData, this.bubbleChartData, this.groupByTimeRange, this.graphUtils, this.rangeSelector);
        this.chartOptions$.next(chartOptions);
    }

    changeTimeSector = (timeRange: RangeSelectorType) => {
        this.rangeSelector = timeRange;
        const chartOptions: Highcharts.Options =  this.bubbleChartConfig(this.moodsByDay, this.averageMoodData, this.bubbleChartData, this.groupByTimeRange, this.graphUtils, this.rangeSelector);
        this.chartOptions$.next(chartOptions);
        this.range.emit(timeRange);
    }

    bubbleChartConfig (moods_by_day: MoodsByDay[], averageMoodData, bubbleChartData, groupData, graphUtils: GraphUtils, timeRange: string): any {

        function xAxisLabels() {
            switch(timeRange) {
                case 'day':
                    return {
                        day: "%a %e/%m"
                    }
                case 'week':
                    return {
                        week: "%a %e/%m"
                    }
                case 'month':
                    return {
                        month: "%m/%Y"
                    }
            }
        }

        function xAxisTickInterval() {
            switch(timeRange) {
                case 'day':
                    return 24 * 3600 * 1000;
                case 'week':
                    return 24 * 3600 * 1000 * 7;
                case 'month':
                    return 24 * 3600 * 1000 * 30;
            }
        }


        return {
            chart: {
                height: 550,
                backgroundColor: graphUtils.graphBackgroundColor,
            },
            title:  {text: ''},
            useHighstock: true,
            plotOptions: {
                series: {
                    stickyTracking: false
                },
                bubble: {
                    enableMouseTracking: false,
                    states: {
                        inactive: {
                            enabled: false
                        }
                    }
                }
            },
            xAxis: {
                type: "datetime",
                dateTimeLabelFormats: xAxisLabels(),
                title: {
                    text: null
                },
                crosshairs: {
                    color: 'black',
                    dashStyle: 'solid'
                },     
                startOnTick: true,
                endOnTick: true,
                tickInterval: xAxisTickInterval(),
            },
            yAxis: {
                title: {
                    text: ''
                },
                labels: {
                    formatter: function () {
                        let icon = 'icon-smileys-excellent';
                        const text = ''
                        switch (this.value) {
                            case 2 :
                                icon = 'icon-smileys-bad';
                                break;
                            case 4 :
                                icon = 'icon-smileys-hard';
                                break;
                            case 6 :
                                icon = 'icon-smileys-average';
                                break;
                            case 8 :
                                icon = 'icon-smileys-good';
                                break;
                            case 10 :
                                icon = 'icon-smileys-excellent';
                                break;
                            default:
                                icon = '';
                        }
        
                        return '<div style="font-size: 1.5em; padding: 0 2rem;"><i class="' + icon + '"></i>'+ text +'</div>';
                    },
                    useHTML: true,
                    offset: 50,
                    y: 30,
                    z: 1000
                },
                min: 0,
                max: 10
            },
            series: [
                {
                    type: 'bubble',
                    name: this.translateService.instant('excellent'),
                    data: bubbleChartData(groupData(moods_by_day, timeRange, graphUtils), 'excellent', this.translateService.instant('excellent'), this.translateService.instant('day'), graphUtils),
                    color: graphUtils.colours('excellent'),
                },
                {
                    type: 'bubble',
                    name: this.translateService.instant('good'),
                    data: bubbleChartData(groupData(moods_by_day, timeRange, graphUtils), 'good', this.translateService.instant('good'), this.translateService.instant('day'), graphUtils),
                    color: graphUtils.colours('good'), 
                },
                {
                    type: 'bubble',
                    name: this.translateService.instant('average'),
                    data: bubbleChartData(groupData(moods_by_day, timeRange, graphUtils), 'average', this.translateService.instant('average'), this.translateService.instant('day'), graphUtils),
                    color: graphUtils.colours('average'),
                },
                {
                    type: 'bubble',
                    name: this.translateService.instant('hard'),
                    data: bubbleChartData(groupData(moods_by_day, timeRange, graphUtils), 'hard', this.translateService.instant('hard'), this.translateService.instant('day'), graphUtils),
                    color: graphUtils.colours('hard'),
                  
                },
                {
                    type: 'bubble',
                    name: this.translateService.instant('bad'),
                    data: bubbleChartData(groupData(moods_by_day, timeRange, graphUtils), 'bad', this.translateService.instant('bad'), this.translateService.instant('day'), graphUtils),
                    color: graphUtils.colours('bad'),
                },
                {
                    type: 'spline',
                    name: this.translateService.instant('average_graph_legend'),
                    data: averageMoodData(groupData(moods_by_day, timeRange, graphUtils), graphUtils, this.translateService),
                    color: '#4892d2',
                    showInLegend: false,
                    lineWidth: 7,
                    states: {
                        hover: {
                            lineWidth: 7
                        }
                    },
                    marker: {
                        enabled: true
                    },
                    tooltip: {
                        headerFormat: '', // to remove xAxis additional tooltip
                        pointFormatter: (function(component) { // use to get access to translateService
                            return function() {
                                      const point = this; // this refers to the hovered point
                                      if(point && component){
                                        return '<div class="moodDistributionTooltip">' +
                                        '<em style="font-size:13px; margin-bottom: -5px">' + point.startDate + ' ' +  point.endDate +'</em><br>' +
                                        '---<br>'+
                                        '<b>' + component.translateService.instant('moods_average') + ':<br> <span style="color:' + point.ycolor + ';">&#9632;</span> ' + point.y.toFixed(2)+  ' <sup>/10</sup></b><br>'
                                        + '<span>---</span><br>'
                                        + '<b>'+ component.translateService.instant('analytics.mood_detail')+': </b><br>' +
                                        '<span style="color: #1abc9c">&#9632;</span> '+ point.moodExc +'<br>'+
                                        '<span style="color: #5c91df">&#9632;</span> '+ point.moodGood + '<br>' +
                                        '<span style="color: #f1c40f">&#9632;</span> '+ point.moodAv + '<br>' +
                                        '<span style="color: #ff7a23">&#9632;</span> '+ point.moodHard + '<br>' +
                                        '<span style="color: #b33c6b">&#9632;</span> ' + point.moodBad + '<br>' +
                                        '<span>---</span><br>'+
                                        '<div style="padding-right: 8px"><b>'+ component.translateService.instant('analytics.show_comments') +': </b><br>' + point.comments + '<br></div>' +
                                    '</div>'
                                }                              
                            }
                        })(this),
                    },
                },
            ],
            tooltip: {
                enabled: true,
                outside: false,
                split: true,
                useHTML: true,
                hideDelay: 8000,
                backgroundColor: 'white',
                borderColor: '#ddd',
                crosshairs: {
                    color: 'black',
                    dashStyle: 'solid'
                }  
            },
        };
    };

    averageMoodData (moods_by_day: MoodsByTimeRange[], graphUtils: GraphUtils, translateService: TranslateService) {
        return sortBy(moods_by_day.map(day => {

            const total = graphUtils.moods_total(day.moodCount);
            const averageOfDay = (day.moodCount.excellent * 10 + day.moodCount.good * 7.5 + day.moodCount.average * 5 + day.moodCount.hard * 2.5) / total;

            const commentsOfDay =
                day.comments
                    .filter((comment) => {
                        return comment.comment && comment.comment !== '';
                    }).map((comment) => {
                        // let transformedComment = $sanitize(comment.comment);
                        let transformedComment = comment.comment;
                        transformedComment = '&#149; "' + graphUtils.wordwrap(transformedComment, 26, '<br>') +'"';
                        transformedComment += ' <span style="color: ' + graphUtils.colours(comment.mood) + '">&#9632;</span>';
                        transformedComment += ' <a href="' + comment.url + '"><i class="fa-regular fa-arrow-up-right-from-square"></i></a>';
                    return transformedComment;
                }).join('<br>');

            const percentage = (mood) => (mood / total * 100).toFixed(0);

            if (commentsOfDay !== '') {
                return {
                    ycolor: graphUtils.scoreToColor(averageOfDay),
                    x: graphUtils.epoch(day.date),
                    y: averageOfDay,
                    startDate: format(parse(day.date, 'dd-MM-yyyy', new Date()), "E dd MMM"),
                    endDate: day.endDate || '',
                    marker: {
                        symbol: 'url(/assets/images/comment-good.svg)',
                        width: 50,
                        height: 50
                    },
                    comments: commentsOfDay,
                    moodExc: day.moodCount.excellent + ' ' + translateService.instant('excellent') + graphUtils.addS(day.moodCount.excellent) +' ('+ percentage(day.moodCount.excellent)+ '%)',
                    moodGood: day.moodCount.good + ' ' + translateService.instant('good') + graphUtils.addS(day.moodCount.good) +' ('+ percentage(day.moodCount.good)+ '%)',
                    moodAv: day.moodCount.average + ' ' + translateService.instant('average') + graphUtils.addS(day.moodCount.average) +' ('+ percentage(day.moodCount.average)+ '%)',
                    moodHard: day.moodCount.hard + ' ' + translateService.instant('hard') + graphUtils.addS(day.moodCount.hard) +' ('+ percentage(day.moodCount.hard)+ '%)',
                    moodBad: day.moodCount.bad + ' ' + translateService.instant('bad') + graphUtils.addS(day.moodCount.bad) +' ('+ percentage(day.moodCount.bad)+ '%)',
                };
            } else {
                return {
                    ycolor: graphUtils.scoreToColor(averageOfDay),
                    x: graphUtils.epoch(day.date),
                    y: averageOfDay,
                    startDate: format(parse(day.date, 'dd-MM-yyyy', new Date()), "E dd MMM"),
                    endDate: day.endDate || '',
                    marker: {
                        enabled: false
                    },
                    comments: '<em>'+translateService.instant('analytics.no_comments')+'</em><br>',
                    moodExc: day.moodCount.excellent + ' ' + translateService.instant('excellent') + graphUtils.addS(day.moodCount.excellent) +' ('+ percentage(day.moodCount.excellent)+ '%)',
                    moodGood: day.moodCount.good + ' ' + translateService.instant('good') + graphUtils.addS(day.moodCount.good) +' ('+ percentage(day.moodCount.good)+ '%)',
                    moodAv: day.moodCount.average + ' ' + translateService.instant('average') + graphUtils.addS(day.moodCount.average) +' ('+ percentage(day.moodCount.average)+ '%)',
                    moodHard: day.moodCount.hard + ' ' + translateService.instant('hard') + graphUtils.addS(day.moodCount.hard) +' ('+ percentage(day.moodCount.hard)+ '%)',
                    moodBad: day.moodCount.bad + ' ' + translateService.instant('bad') + graphUtils.addS(day.moodCount.bad) +' ('+ percentage(day.moodCount.bad)+ '%)',
                };
            }
        }), ['x']);
    };

    bubbleChartData (moods_by_day: MoodsByDay[], moodType: string, moodTypeTranslated: string, dayTranslated: string, graphUtils: GraphUtils) {
        return flatten(sortBy(moods_by_day.map(day => {

            const toBubble = (mood) => {
                if (day.moodCount[mood] > 0) {
                    const s = day.moodCount[mood] > 1 ? 's' : '';
                    return [{
                        name: ' ' + dayTranslated.toLowerCase() + s + ' ' + moodTypeTranslated.toLowerCase() + s,
                        x: graphUtils.epoch(day.date),
                        y: graphUtils.plotValues(mood),
                        yColor: 'red',
                        z: day.moodCount[mood]
                    }];
                } else {
                    return [];
                }
            };

            return toBubble(moodType);

        }), ['x']));
    };
    groupByTimeRange (mood_by_day: MoodsByDay[], timeRange: string ='day', graphUtils: GraphUtils): MoodsByTimeRange[] {
        if(mood_by_day && mood_by_day.length > 0){
            mood_by_day = sortBy(mood_by_day, mood => getUnixTime(parse(mood.date, 'dd-MM-yyyy', new Date())));

            if (timeRange === 'day') {
                return mood_by_day;
            }

            const firstDayOfSelection = parse(mood_by_day[0].date,  'dd-MM-yyyy', new Date());
            const lastDayOfSelection = parse(mood_by_day[mood_by_day.length-1].date,  'dd-MM-yyyy', new Date());
            let dateIntervals;

            if(timeRange === 'week'){
                dateIntervals = graphUtils.createDateInterval(firstDayOfSelection, lastDayOfSelection, graphUtils.findNextMonday);
            }
            if(timeRange === 'month'){
                dateIntervals = graphUtils.createDateInterval(firstDayOfSelection, lastDayOfSelection, graphUtils.findFirstDayOfNextMonth);
            }            

            const moodByDayByInterval = dateIntervals.map(interval =>{
                return {
                    interval: { start: interval.start, end: interval.end},
                    moods: mood_by_day.filter(day => {
                        return graphUtils.isDateInsideDateRange(day.date, interval.start, interval.end);
                    })
                }
            })

            let moodByDayByIntervalReduced = moodByDayByInterval.map(subObj => {
                if(subObj.moods.length > 0){
                    return graphUtils.reduceArray(subObj.moods, subObj.interval.start, subObj.interval.end);
                }
            });
            moodByDayByIntervalReduced = moodByDayByIntervalReduced.filter(mood => mood !== undefined);

            return moodByDayByIntervalReduced;
        }else{
            return [];
        }
    }
}

type RangeSelectorType = 'day' | 'week' | 'month'

